"use client";
import { useState } from "react";

import { PoolName } from "@/shared/constants/pools";
import { ChainName } from "@/shared/types/networks";

import { ChartSection } from "./components/chart-section";
import { DifferentVolatilityChart } from "./components/different-volatility-chart";
import { HistoricalVolatilityChart } from "./components/historical-volatility-chart";
import { LeverageChart } from "./components/leverage-chart";
import { LiquidationMapChart } from "./components/liquidation-map-chart";
import { LiquidationsChart } from "./components/liquidations-chart";
import { LiquidityChart } from "./components/liquidity-chart";
import { LogReturnsChart } from "./components/log-returns-chart";
import { OpenInterestChart } from "./components/open-interest-chart";
import { PoolTabGroupe } from "./components/pool-tab-groupe";
import { SimulatedVolatilityChart } from "./components/simulated-volatility-chart";
import { TradingVolumesChart } from "./components/trading-volumes-chart";
import { VolatilityConesChart } from "./components/volatility-cones-chart";
import { useChartData } from "./hooks/use-chart-data";
import { useLiquidationData } from "./hooks/use-liquidations-data";
import { useLiquidityChartData } from "./hooks/use-liquidity-chart-data";
import { useTradingData } from "./hooks/use-trading-data";
import { useTradingDataWithRates } from "./hooks/use-trading-data-with-rates";
import * as S from "./styled";

const DAYS_IN_WEEK = 7;

interface Props {
  chainName: ChainName;
}

export const ChartList = ({ chainName }: Props) => {
  const [activePool, setActivePool] = useState<PoolName>(PoolName.WethUsdc);
  const { data: chartData, hasCoreCharts } = useChartData(
    activePool,
    chainName,
  );
  const { data: liquidityChartData, hasLiquidityChart } = useLiquidityChartData(
    activePool,
    chainName,
  );
  const logReturns = chartData?.logReturns;

  const tradingDataWithoutRates = useTradingData(activePool, chainName);

  const tradingData = useTradingDataWithRates({
    poolName: activePool,
    data: tradingDataWithoutRates,
  });

  const liquidationData = useLiquidationData(activePool, chainName);
  const intraDayVolatilityCones = chartData?.volatilityCones.filter(
    (cone) => cone.period <= DAYS_IN_WEEK,
  );

  const dailyVolatilityCones = chartData?.volatilityCones.filter(
    (cone) => cone.period > DAYS_IN_WEEK,
  );

  return (
    <div>
      <S.Title>Dashboard</S.Title>
      <S.SelectWrapper>
        <S.SelectInner>
          <PoolTabGroupe
            onChange={setActivePool}
            value={activePool}
            chainName={chainName}
          />
        </S.SelectInner>
      </S.SelectWrapper>
      {hasCoreCharts ? (
        <>
          <S.Row>
            <ChartSection
              title="Log returns"
              description="Historical logarithmic returns for a selected trading pair"
              chartSlot={<LogReturnsChart data={logReturns} />}
            />
            <ChartSection
              title="Different volatility measures"
              description="Different historical volatility measures using a 30-day rolling
            window."
              chartSlot={<DifferentVolatilityChart data={logReturns} />}
            />
          </S.Row>
          <S.Row>
            <ChartSection
              title="Daily volatility cones"
              description="Volatility quantiles were calculated using non-overlapping daily
            intervals. (20 to 180 days). STD volatility measure was used
            (square root of variance)."
              chartSlot={<VolatilityConesChart data={dailyVolatilityCones} />}
            />
            <ChartSection
              title="Intraday volatility cones"
              description="Volatility quantiles were calculated using non-overlapping hour
            intervals. (1 to 7 days). STD volatility measure was used."
              chartSlot={
                <VolatilityConesChart data={intraDayVolatilityCones} />
              }
            />
          </S.Row>
          <S.Row>
            <ChartSection
              title="Historical volatility histogram"
              description="Distribution of historical volatility values. STD volatility
            measure was used (square root of variance)."
              chartSlot={
                <HistoricalVolatilityChart
                  data={chartData?.historicalVolatilityHistogram}
                />
              }
            />
            <ChartSection
              title="Simulated returns histogram"
              description="Simulation of worst 1% returns distribution. Merton Jump-Diffusion
            process is used to simulate returns."
              chartSlot={
                <SimulatedVolatilityChart
                  data={chartData?.simulatedVolatilityHistogram}
                />
              }
            />
          </S.Row>
        </>
      ) : null}
      {hasLiquidityChart ? (
        <S.Row>
          <ChartSection
            title="Uniswap Liquidity and Implied volatility"
            description="Uniswap liquidity distribution by price and corresponding implied
            volatility given the pool fee tier."
            chartSlot={<LiquidityChart data={liquidityChartData} />}
          />
        </S.Row>
      ) : null}
      <S.Row>
        <ChartSection
          title="Trading volume"
          description="Historical long and short trading volume"
          chartSlot={<TradingVolumesChart data={tradingData} />}
        />
      </S.Row>
      <S.Row>
        <ChartSection
          title="Open interest"
          description="Historical long and short open interest"
          chartSlot={<OpenInterestChart data={tradingData} />}
        />
        <ChartSection
          title="Leverage"
          description="Historical long and short open aggregate leverage values"
          chartSlot={<LeverageChart data={tradingData} />}
        />
      </S.Row>
      <S.Row>
        <ChartSection
          title="Liquidations"
          description="Historical long and short liquidation volumes"
          chartSlot={<LiquidationsChart data={tradingData} />}
        />
      </S.Row>
      <S.Row>
        <ChartSection
          title="Liquidation map"
          description="Current long and short liquidation map"
          chartSlot={<LiquidationMapChart data={liquidationData} />}
        />
      </S.Row>
    </div>
  );
};
